/**
 * A plan map's part in the onboarding handover (`spec-onboarding-persistent-scene-handover`):
 *
 * - **It offers a capture** while it is loaded, on screen and drawn, for the section setter to hold
 *   before the step changes (R3.1). Withdrawn on hide or unmount.
 * - **It announces itself** as it mounts, in a layout effect, so R3.3's wait counts from the commit.
 * - **It reports arrival:** once its first camera is applied and it has drawn, the next `idle`
 *   (R3.2); a failed draw at once (R3.5).
 *
 * - **An editor map reveals itself** (`spec-onboarding-persistent-scene-handover-glass-align`, R2):
 *   with `reveals`, a map arriving into a hold is invisible until it arrives, then fades in over
 *   the picture, which is removed when that fade ends. The host puts `revealProps` on its drawing.
 *
 * Used by the four plan maps a step change joins: the editor (Floor Plan, Camera Feeds), the
 * Observers stage plan, Position on map's map and the washed plan on the onboarding stage. Every one
 * holds its picture on the page's backdrop (`onboardingPictureHost`, glass-align R1).
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useSyncExternalStore,
  type CSSProperties,
  type TransitionEvent,
} from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import { motion } from '@repo/design-tokens';
import { composeMapPicture } from './readMapPicture';
import type { PlanDrawState } from './planDrawState';
import {
  FADE_MARGIN_MS,
  FADE_MS,
  FRAME_MS,
  announceArrival,
  fadeFor,
  finishRelease,
  getHandover,
  holdInProgress,
  prefersReducedMotion,
  registerCapture,
  registeredCapture,
  reportArrived,
  revealFor,
  subscribeHandover,
  warnCaptureFailed,
} from './planHandover';
import type { HeldPicture, RevealState } from './planHandover';

/**
 * Glass-align R1.2: where an onboarding plan map's picture is held, the page's backdrop, under the
 * glass header, the step list, the panels and the wash. A stage map sits in the backdrop; an editor
 * map sits in the step's content, so the page's one backdrop is found in the document. `null`
 * outside onboarding (no backdrop), where the picture is held over the page (R1.3).
 */
export const onboardingPictureHost = (
  map: Pick<MapboxMap, 'getContainer'> | null,
): HTMLElement | null => {
  try {
    const own = map?.getContainer().closest<HTMLElement>('[data-onboarding-backdrop]') ?? null;
    if (own) return own;
  } catch {
    // The map is being removed: the document's backdrop is the same element.
  }
  return document.querySelector<HTMLElement>('[data-onboarding-backdrop]');
};

/** The map calls the handover makes. */
type HandoverMap = Pick<
  MapboxMap,
  'getCanvas' | 'getCanvasContainer' | 'triggerRepaint' | 'once' | 'off'
>;

/*
 * The joining maps keep their drawing buffer (`PlanMapHost`'s `preserveDrawingBuffer`), so the
 * capture reads both canvases at once, in the press that changes the step. R5's measurement chose
 * this over waiting for the map's next frame (2026-10-31, UGG-FIN at 1440 × 900): a median 2.3 ms
 * and worst 22.8 ms against 27.7 ms and 48.6 ms, with no change in a pan's frame times (median and
 * 95th percentile 16.7 ms and 16.8 ms either way).
 */

/** The development probe's sample, in pixels a side, for how much of a picture is opaque. */
const SAMPLE_PX = 16;

/**
 * How far the plan's canvas is shrunk to tell whether it drew anything: a quarter of each side. A
 * coarser sample (16 × 16 was tried) can fall between a plan's thin lines and read a drawn plan as
 * blank, which held nothing on a real step change (`handover.ab.pw.spec.ts`, 2026-10-31).
 */
const BLANK_SAMPLE_DIVISOR = 4;

/** Did this canvas draw nothing at all (every sampled pixel transparent)? `null`: cannot tell. */
const isBlank = (canvas: HTMLCanvasElement): boolean | null => {
  const sample = document.createElement('canvas');
  sample.width = Math.max(1, Math.ceil(canvas.width / BLANK_SAMPLE_DIVISOR));
  sample.height = Math.max(1, Math.ceil(canvas.height / BLANK_SAMPLE_DIVISOR));
  const ctx = sample.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(canvas, 0, 0, sample.width, sample.height);
  const { data } = ctx.getImageData(0, 0, sample.width, sample.height);
  for (let i = 3; i < data.length; i += 4) if (data[i] !== 0) return false;
  return true;
};

/**
 * The map's two canvases, or `null` when either is missing, detached or zero-sized (spec Inputs).
 * react-three-map mounts its overlay's canvas beside the map's, in the map's canvas container.
 */
export const planMapCanvases = (
  map: Pick<MapboxMap, 'getCanvas' | 'getCanvasContainer'>,
): { base: HTMLCanvasElement; overlay: HTMLCanvasElement } | null => {
  const base = map.getCanvas();
  if (!base || !base.isConnected || base.width === 0 || base.height === 0) return null;
  const overlay =
    Array.from(map.getCanvasContainer().querySelectorAll('canvas')).find(c => c !== base) ?? null;
  if (!overlay || overlay.width === 0 || overlay.height === 0) return null;
  return { base, overlay };
};

/** Is a rectangle a real, on-screen one: finite, non-zero, and inside the viewport somewhere? */
const onScreen = (r: DOMRect): boolean =>
  [r.left, r.top, r.width, r.height].every(Number.isFinite) &&
  r.width > 0 &&
  r.height > 0 &&
  r.right > 0 &&
  r.bottom > 0 &&
  r.left < window.innerWidth &&
  r.top < window.innerHeight;

/**
 * Composite the map as shown now, or `null` (nothing held). Never throws. A map being removed holds
 * nothing without a warning; the one warning is kept for a canvas that cannot be read.
 */
export const composeHeldPicture = (
  map: HandoverMap,
  host: HTMLElement | null = null,
): HeldPicture | null => {
  let canvases: ReturnType<typeof planMapCanvases>;
  let box: DOMRect;
  try {
    canvases = planMapCanvases(map);
    if (!canvases) return null;
    box = canvases.base.getBoundingClientRect();
  } catch {
    return null;
  }
  if (!onScreen(box)) return null;
  const { base, overlay } = canvases;
  try {
    // The plan itself: a picture without it would show the plan vanishing (spec Inputs).
    const overlayBlank = isBlank(overlay);
    if (overlayBlank === null) {
      warnCaptureFailed('no-2d-context');
      return null;
    }
    if (overlayBlank) return null;
    const canvas = composeMapPicture(base, overlay, base.width, base.height, {
      baseShown: base.style.visibility !== 'hidden',
    });
    if (!canvas) {
      warnCaptureFailed('no-2d-context');
      return null;
    }
    return {
      canvas,
      rect: { left: box.left, top: box.top, width: box.width, height: box.height },
      ...(host && { host }),
    };
  } catch {
    warnCaptureFailed('canvas-unreadable');
    return null;
  }
};

export interface HandoverJoinOptions {
  /** The host's loaded map, or `null`. */
  map: HandoverMap | null;
  /** The map's first camera has been applied (the handed pose, or the map's own fit). */
  ready: boolean;
  /** The host's draw state. */
  drawState: PlanDrawState;
  /** False for a plan map that joins no step change (the corner plan, the pictures). */
  enabled?: boolean;
  /**
   * Where the picture of this map is held, read as it is taken (`HeldPicture.host`). Absent: over
   * the page.
   */
  pictureHost?: () => HTMLElement | null;
  /**
   * Glass-align R2.2: an editor map, which arrives invisible over the picture and fades in. A stage
   * map (false) is shown as it draws and the picture fades out over it (R3).
   */
  reveals?: boolean;
}

/** What the host spreads on the element holding its drawing (the map and what is drawn over it). */
export interface RevealProps {
  style: CSSProperties | undefined;
  'data-handover-reveal': RevealState;
  onTransitionEnd: ((e: TransitionEvent<HTMLElement>) => void) | undefined;
}

/**
 * Glass-align R2.2 to R2.5: this map's reveal. Invisible over the picture until its arrival; then
 * a fade over `motion.durFast` (cut to the time left near the cap), after which the picture goes.
 * Under reduced motion, or a fade too short to see, the map shows and the picture goes in the same
 * frame. A map unmounting mid-fade leaves the picture for the newer map (Overtaken).
 */
function useHandoverReveal(
  enabled: boolean,
  generationRef: { readonly current: number | null },
  heldRef: { readonly current: boolean },
): RevealProps {
  /*
   * Read as a primitive (the state, and a fade's length), so the host re-renders only when what it
   * shows changes, not on every change to the hold: the hosts are the plan maps' largest components.
   */
  const read = (): string => {
    const generation = generationRef.current;
    if (!enabled || generation === null) return 'shown';
    const held = getHandover();
    const state = revealFor(held, generation, heldRef.current);
    if (state !== 'revealing' || !held || held.revealStartedAt === null) return state;
    return `revealing:${fadeFor(held.heldAt, held.revealStartedAt, held.capMs)}`;
  };
  const snapshot = useSyncExternalStore(subscribeHandover, read, read);
  const [state, fadeText] = snapshot.split(':') as [RevealState, string | undefined];
  const fadeMs = fadeText === undefined ? FADE_MS : Number(fadeText);
  const cut = state === 'revealing' && (fadeMs < FRAME_MS || prefersReducedMotion());

  /**
   * The fade has ended: the picture goes, if this map's fade is still the one under way and no newer
   * join has taken the hold (an overtaken map never takes the picture from the newer one).
   */
  const finish = useCallback(() => {
    const now = getHandover();
    if (
      now &&
      !now.releasing &&
      now.generation === generationRef.current &&
      now.revealGeneration === generationRef.current
    ) {
      finishRelease(now.picture);
    }
  }, [generationRef]);

  // R2.3: a cut removes the picture in the commit the map is shown in; a fade has a fallback should
  // `transitionend` never come. Cleanup never removes the picture (Overtaken: a newer map needs it).
  useLayoutEffect(() => {
    if (state !== 'revealing') return undefined;
    if (cut) {
      finish();
      return undefined;
    }
    const fallback = setTimeout(finish, fadeMs + FADE_MARGIN_MS);
    return () => clearTimeout(fallback);
  }, [state, cut, fadeMs, finish]);

  if (state === 'shown') {
    return { style: undefined, 'data-handover-reveal': 'shown', onTransitionEnd: undefined };
  }
  if (state === 'hidden') {
    return { style: { opacity: 0 }, 'data-handover-reveal': 'hidden', onTransitionEnd: undefined };
  }
  return {
    style: cut
      ? { opacity: 1 }
      : {
          opacity: 1,
          transition: `opacity ${fadeMs < FADE_MS ? `${fadeMs}ms` : motion.durFast} ${motion.easeUi}`,
        },
    'data-handover-reveal': 'revealing',
    onTransitionEnd: e => {
      if (e.target === e.currentTarget && e.propertyName === 'opacity') finish();
    },
  };
}

export function useHandoverJoin({
  map,
  ready,
  drawState,
  enabled = true,
  pictureHost,
  reveals = false,
}: HandoverJoinOptions): RevealProps {
  const pictureHostRef = useRef(pictureHost);
  pictureHostRef.current = pictureHost;
  const generationRef = useRef<number | null>(null);
  /** Glass-align R2.8: whether this map arrived into a hold; one that did not shows at once. */
  const heldRef = useRef(false);
  // R3.3: announced as the step commits, once per mount.
  useLayoutEffect(() => {
    if (enabled && generationRef.current === null) {
      heldRef.current = reveals && holdInProgress();
      generationRef.current = announceArrival({ reveals });
    }
    // `reveals` is read once, with the announcement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // R3.2, R3.5: arrival, once per mount.
  const reportedRef = useRef(false);
  const drawKind = drawState.kind;
  useEffect(() => {
    const generation = generationRef.current;
    if (!enabled || generation === null || reportedRef.current) return undefined;
    if (drawKind === 'failed') {
      reportedRef.current = true;
      reportArrived(generation, 'failed');
      return undefined;
    }
    if (!map || !ready || drawKind !== 'drawn') return undefined;
    const onIdle = () => {
      reportedRef.current = true;
      reportArrived(generation, 'arrived');
    };
    map.once('idle', onIdle);
    try {
      // A map already idle at an unchanged camera fires no further `idle` without a frame.
      map.triggerRepaint();
    } catch {
      // The map is being removed.
    }
    return () => {
      map.off('idle', onIdle);
    };
  }, [enabled, map, ready, drawKind]);

  // R3.1: the capture, offered while the map is on screen and drawn.
  useEffect(() => {
    if (!enabled || !map || !ready || drawKind !== 'drawn') return undefined;
    return registerCapture(() => composeHeldPicture(map, pictureHostRef.current?.() ?? null));
  }, [enabled, map, ready, drawKind]);

  return useHandoverReveal(enabled && reveals, generationRef, heldRef);
}

/**
 * Development builds only: the measuring rig's probe (R5). Times the registered capture and says how
 * much of its picture is opaque (a blank or partial street map shows as less than all of it).
 */
if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__planHandoverProbe = {
    method: () => 'preserve',
    capture: async () => {
      const fn = registeredCapture();
      if (!fn) return null;
      const started = performance.now();
      const picture = fn();
      const ms = performance.now() - started;
      if (!picture) return { ms, held: false };
      const sample = document.createElement('canvas');
      sample.width = SAMPLE_PX;
      sample.height = SAMPLE_PX;
      const ctx = sample.getContext('2d');
      let opaque = 0;
      if (ctx) {
        ctx.drawImage(picture.canvas, 0, 0, SAMPLE_PX, SAMPLE_PX);
        const { data } = ctx.getImageData(0, 0, SAMPLE_PX, SAMPLE_PX);
        for (let i = 3; i < data.length; i += 4) if (data[i] === 255) opaque += 1;
      }
      const result = {
        ms,
        held: true,
        width: picture.canvas.width,
        height: picture.canvas.height,
        rect: picture.rect,
        opaqueShare: opaque / (SAMPLE_PX * SAMPLE_PX),
      };
      picture.canvas.width = 0;
      return result;
    },
  };
}
